import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ApprovalRequestId, type ChatAttachment, type ThreadId } from "@cmmd-forge/contracts";
import {
  setPendingUserInputCustomAnswer,
  togglePendingUserInputOptionSelection,
  type PendingUserInputDraftAnswer,
} from "../../pendingUserInput";
import { type PendingUserInput } from "../../session-logic";
import type { ComposerFileAttachment } from "../../composerDraftStore";
import { prepareComposerFileAttachments } from "../../lib/prepareComposerFileAttachments";
import { releaseUnreferencedComposerFile } from "../../composerFileAttachmentLifecycle";
import type { EnvironmentId } from "@cmmd-forge/contracts";
import { deriveActivePendingUserInputState } from "../ChatView.logic";
import { type ChatComposerHandle } from "./ChatComposerHandle";
import {
  clearPendingUserInputDrafts,
  readPendingUserInputDrafts,
  writePendingUserInputDrafts,
} from "../../pendingUserInputDraftStore";

export function usePendingUserInputControls(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly pendingUserInputs: ReadonlyArray<PendingUserInput>;
  readonly respondingUserInputRequestIds: ReadonlyArray<ApprovalRequestId>;
  readonly promptRef: { current: string };
  readonly composerRef: { current: ChatComposerHandle | null };
  readonly onRespondToUserInput: (
    requestId: ApprovalRequestId,
    answers: Record<string, unknown>,
    attachmentsByQuestionId?: Record<string, readonly ChatAttachment[]>,
  ) => Promise<void>;
}): ReturnType<typeof deriveActivePendingUserInputState> & {
  readonly onSelectActivePendingUserInputOption: (questionId: string, optionLabel: string) => void;
  readonly onChangeActivePendingUserInputCustomAnswer: (
    questionId: string,
    value: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
  ) => void;
  readonly onAdvanceActivePendingUserInput: () => void;
  readonly onPreviousActivePendingUserInputQuestion: () => void;
  readonly onQuestionFilesChange: (questionId: string, files: ComposerFileAttachment[]) => void;
  readonly pendingUserInputError: string | null;
} {
  const [pendingUserInputAnswersByRequestId, setPendingUserInputAnswersByRequestId] = useState<
    Record<string, Record<string, PendingUserInputDraftAnswer>>
  >({});
  const [pendingUserInputQuestionIndexByRequestId, setPendingUserInputQuestionIndexByRequestId] =
    useState<Record<string, number>>({});
  const [locallyRespondingRequestIds, setLocallyRespondingRequestIds] = useState<string[]>([]);
  const [pendingUserInputError, setPendingUserInputError] = useState<string | null>(null);
  const submissionLocksRef = useRef(new Set<string>());
  const answersByRequestRef = useRef(pendingUserInputAnswersByRequestId);
  answersByRequestRef.current = pendingUserInputAnswersByRequestId;
  const draftScopeRef = useRef<string | null>(null);

  useEffect(() => {
    const restored = Object.fromEntries(
      input.pendingUserInputs.map((pending) => [
        pending.requestId,
        readPendingUserInputDrafts({
          environmentId: input.environmentId,
          threadId: input.threadId,
          requestId: pending.requestId,
        }),
      ]),
    );
    const scope = `${input.environmentId}:${input.threadId}`;
    const next =
      draftScopeRef.current === scope ? { ...restored, ...answersByRequestRef.current } : restored;
    draftScopeRef.current = scope;
    answersByRequestRef.current = next;
    setPendingUserInputAnswersByRequestId(next);
  }, [input.environmentId, input.pendingUserInputs, input.threadId]);

  const updateDraftAnswers = useCallback(
    (
      requestId: string,
      update: (
        drafts: Record<string, PendingUserInputDraftAnswer>,
      ) => Record<string, PendingUserInputDraftAnswer>,
    ) => {
      const drafts = update(answersByRequestRef.current[requestId] ?? {});
      const next = { ...answersByRequestRef.current, [requestId]: drafts };
      answersByRequestRef.current = next;
      setPendingUserInputAnswersByRequestId(next);
      if (
        !writePendingUserInputDrafts({
          environmentId: input.environmentId,
          threadId: input.threadId,
          requestId,
          drafts,
        })
      ) {
        setPendingUserInputError("Could not save this response in your browser.");
      }
    },
    [input.environmentId, input.threadId],
  );

  const activeState = useMemo(
    () =>
      deriveActivePendingUserInputState({
        pendingUserInputs: input.pendingUserInputs,
        draftAnswersByRequestId: pendingUserInputAnswersByRequestId,
        questionIndexByRequestId: pendingUserInputQuestionIndexByRequestId,
        respondingRequestIds: [
          ...input.respondingUserInputRequestIds,
          ...locallyRespondingRequestIds,
        ],
      }),
    [
      input.pendingUserInputs,
      input.respondingUserInputRequestIds,
      locallyRespondingRequestIds,
      pendingUserInputAnswersByRequestId,
      pendingUserInputQuestionIndexByRequestId,
    ],
  );

  const setActivePendingUserInputQuestionIndex = useCallback(
    (nextQuestionIndex: number) => {
      if (!activeState.activePendingUserInput) {
        return;
      }
      setPendingUserInputQuestionIndexByRequestId((existing) => ({
        ...existing,
        [activeState.activePendingUserInput!.requestId]: nextQuestionIndex,
      }));
    },
    [activeState.activePendingUserInput],
  );

  const onSelectActivePendingUserInputOption = useCallback(
    (questionId: string, optionLabel: string) => {
      if (!activeState.activePendingUserInput) {
        return;
      }
      const question =
        (activeState.activePendingProgress?.activeQuestion?.id === questionId
          ? activeState.activePendingProgress.activeQuestion
          : undefined) ??
        activeState.activePendingUserInput!.questions.find(
          (entry: PendingUserInput["questions"][number]) => entry.id === questionId,
        );
      if (!question) return;
      updateDraftAnswers(activeState.activePendingUserInput.requestId, (existing) => ({
        ...existing,
        [questionId]: togglePendingUserInputOptionSelection(
          question,
          existing[questionId],
          optionLabel,
        ),
      }));
      input.promptRef.current = "";
      input.composerRef.current?.resetCursorState({ cursor: 0 });
    },
    [
      activeState.activePendingProgress?.activeQuestion,
      activeState.activePendingUserInput,
      input.composerRef,
      input.promptRef,
      updateDraftAnswers,
    ],
  );

  const onChangeActivePendingUserInputCustomAnswer = useCallback(
    (
      questionId: string,
      value: string,
      nextCursor: number,
      expandedCursor: number,
      _cursorAdjacentToMention: boolean,
    ) => {
      if (!activeState.activePendingUserInput) {
        return;
      }
      input.promptRef.current = value;
      updateDraftAnswers(activeState.activePendingUserInput.requestId, (existing) => ({
        ...existing,
        [questionId]: setPendingUserInputCustomAnswer(existing[questionId], value),
      }));
      const snapshot = input.composerRef.current?.readSnapshot();
      if (
        snapshot?.value !== value ||
        snapshot.cursor !== nextCursor ||
        snapshot.expandedCursor !== expandedCursor
      ) {
        input.composerRef.current?.focusAt(nextCursor);
      }
    },
    [activeState.activePendingUserInput, input.composerRef, input.promptRef, updateDraftAnswers],
  );

  const onQuestionFilesChange = useCallback(
    (questionId: string, files: ComposerFileAttachment[]) => {
      if (!activeState.activePendingUserInput) return;
      updateDraftAnswers(activeState.activePendingUserInput.requestId, (existing) => ({
        ...existing,
        [questionId]: { ...existing[questionId], attachments: files },
      }));
    },
    [activeState.activePendingUserInput, updateDraftAnswers],
  );

  const submitActivePendingUserInput = async () => {
    const pending = activeState.activePendingUserInput;
    const answers = activeState.activePendingResolvedAnswers;
    if (!pending || !answers || submissionLocksRef.current.has(pending.requestId)) return;

    submissionLocksRef.current.add(pending.requestId);
    setLocallyRespondingRequestIds((existing) =>
      existing.includes(pending.requestId) ? existing : [...existing, pending.requestId],
    );
    setPendingUserInputError(null);
    try {
      const filesByQuestion = Object.entries(activeState.activePendingDraftAnswers)
        .map(([questionId, answer]) => ({
          questionId,
          files: answer.attachments ?? [],
        }))
        .filter((entry) => entry.files.length > 0);
      const files = filesByQuestion.flatMap((entry) => entry.files);
      if (files.length > 8) throw new Error("You can attach up to 8 files per response.");
      const uploaded = await prepareComposerFileAttachments({
        environmentId: input.environmentId,
        files,
      });
      let offset = 0;
      const attachmentsByQuestionId = Object.fromEntries(
        filesByQuestion.map(({ questionId, files }) => {
          const attachments = uploaded.slice(offset, offset + files.length);
          offset += files.length;
          return [questionId, attachments];
        }),
      );
      await input.onRespondToUserInput(pending.requestId, answers, attachmentsByQuestionId);
      clearPendingUserInputDrafts({
        environmentId: input.environmentId,
        threadId: input.threadId,
        requestId: pending.requestId,
      });
      await Promise.all(files.map((attachment) => releaseUnreferencedComposerFile(attachment.id)));
    } catch (error) {
      setPendingUserInputError(
        error instanceof Error ? error.message : "Could not send your response. Try again.",
      );
    } finally {
      submissionLocksRef.current.delete(pending.requestId);
      setLocallyRespondingRequestIds((existing) =>
        existing.filter((requestId) => requestId !== pending.requestId),
      );
    }
  };

  const onAdvanceActivePendingUserInput = () => {
    if (!activeState.activePendingUserInput || !activeState.activePendingProgress) {
      return;
    }
    if (activeState.activePendingProgress.isLastQuestion) {
      if (activeState.activePendingResolvedAnswers) {
        void submitActivePendingUserInput();
      }
      return;
    }
    setActivePendingUserInputQuestionIndex(activeState.activePendingProgress.questionIndex + 1);
  };

  const onPreviousActivePendingUserInputQuestion = useCallback(() => {
    if (!activeState.activePendingProgress) {
      return;
    }
    setActivePendingUserInputQuestionIndex(
      Math.max(activeState.activePendingProgress.questionIndex - 1, 0),
    );
  }, [activeState.activePendingProgress, setActivePendingUserInputQuestionIndex]);

  return {
    ...activeState,
    onSelectActivePendingUserInputOption,
    onChangeActivePendingUserInputCustomAnswer,
    onAdvanceActivePendingUserInput,
    onPreviousActivePendingUserInputQuestion,
    onQuestionFilesChange,
    pendingUserInputError,
  };
}
